import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchAllMediums,
  createMedium,
  updateMedium,
  deleteMedium,
} from "../redux/mediumSlice";
import Popup from "../../../Popup";
import Table from "../../Table";
import API_BASE_URL from "../../config";
import { TableSkeleton } from "../Skeleton";
import showToast from "../../../utils/toast";

const Medium = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showClassPopup, setShowClassPopup] = useState(false);
  const { boardId } = useParams();
  const dispatch = useDispatch();
  const mediums = useSelector((state) => state.mediums.allList);
  const loading = useSelector((state) => state.mediums.loading);

  const [form, setForm] = useState({
    id: null,
    name: "",
    description: "",
    boardId: boardId ? Number(boardId) : "",
  });
  const [isEditing, setIsEditing] = useState(false);

  // Class popup state
  const [className, setClassName] = useState("");
  const [selectedMediumId, setSelectedMediumId] = useState(null);
  const [selectedMediumName, setSelectedMediumName] = useState("");

  useEffect(() => {
    dispatch(fetchAllMediums(boardId));

    if (boardId) {
      setForm((prev) => ({
        ...prev,
        boardId: Number(boardId),
      }));
    }
  }, [dispatch, boardId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    const action = isEditing ? updateMedium(form) : createMedium(form);

    dispatch(action).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(fetchAllMediums(boardId));
        setShowPopup(false);
        cancelEdit();
      }
    });
  };

  const startEdit = (item) => {
    setForm({
      ...item,
      boardId: Number(boardId),
    });
    setIsEditing(true);
    setShowPopup(true);
  };

  const cancelEdit = () => {
    setForm({
      id: null,
      name: "",
      description: "",
      boardId: Number(boardId),
    });
    setIsEditing(false);
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const isCurrentlyActive = currentStatus === "active" || currentStatus === 1;
    const newStatus = isCurrentlyActive ? 0 : 1;
    const label = isCurrentlyActive ? "inactive" : "active";

    if (!window.confirm(`Are you sure you want to mark this medium as "${label}"?`)) return;

    try {
      await fetch(`${API_BASE_URL}/board/medium/status/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, updated_by: 1 }),
      });

      dispatch(fetchAllMediums(boardId));
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this medium?")) return;
    try {
      await dispatch(deleteMedium(id)).unwrap();
      showToast.success("Medium deleted successfully");
      dispatch(fetchAllMediums(boardId));
    } catch (error) {
      showToast.error(error);
    }
  };

  // Handle Add Class
  const handleAddClassClick = (medium) => {
    setSelectedMediumId(medium.id);
    setSelectedMediumName(medium.name);
    setClassName("");
    setShowClassPopup(true);
  };

  const handleAddClass = async () => {
    if (!className.trim()) {
      alert("Please enter class name");
      return;
    }

    try {
      const payload = {
        board_id: boardId,
        medium_id: selectedMediumId,
        name: className,
        created_by: "1",
      };

      await fetch(`${API_BASE_URL}/board/class-create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setClassName("");
      setShowClassPopup(false);
      alert("Class created successfully!");
    } catch (err) {
      console.error("Failed to create class", err);
      alert("Failed to create class");
    }
  };

  const isFormValid = form.name.trim() && form.description.trim();
  const isClassFormValid = className.trim();

  // Define table columns
  const columns = [
    {
      header: "Name",
      accessor: "name",
      align: "left",
    },
    {
      header: "Description",
      accessor: "description",
      align: "left",
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      render: (_, row) => {
        const isActive = row.status === "active" || row.status === 1;
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 14px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              color: isActive ? "#15803d" : "#dc2626",
              backgroundColor: isActive ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`,
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "center",
      render: (_, row) => {
        const isActive = row.status === "active" || row.status === 1;
        return (
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <i
              className="bi bi-pencil-square"
              style={{
                fontSize: "18px",
                color: "#3b82f6",
                cursor: "pointer",
              }}
              title="Edit"
              onClick={() => startEdit(row)}
            />
            <i
              className="bi bi-trash"
              style={{
                fontSize: "18px",
                color: "#dc2626",
                cursor: "pointer",
              }}
              title="Delete"
              onClick={() => handleDelete(row.id)}
            />
            <div
              onClick={() => handleStatusToggle(row.id, row.status)}
              title={isActive ? "Deactivate" : "Activate"}
              style={{
                width: "36px",
                height: "18px",
                borderRadius: "9px",
                backgroundColor: isActive ? "#22c55e" : "#d1d5db",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
            >
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  position: "absolute",
                  top: "2px",
                  left: isActive ? "20px" : "2px",
                  transition: "left 0.3s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }}
              />
            </div>
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() => handleAddClassClick(row)}
              title="Add Class"
              style={{ fontSize: "12px", padding: "4px 10px" }}
            >
              <i className="bi bi-plus-circle"></i> Add Class
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: "600",
            color: "#1f2937",
          }}
        >
          Mediums
        </h2>
        <button
          onClick={() => {
            cancelEdit();
            setShowPopup(true);
          }}
          style={{
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#2563eb")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#3b82f6")}
        >
          <span style={{ fontSize: "18px" }}>+</span> Add Medium
        </button>
      </div>

      {/* TABLE */}
      {loading ? (
        <TableSkeleton rows={5} columns={3} />
      ) : (
        <Table columns={columns} data={mediums} />
      )}

      {/* POPUP */}
      {showPopup && (
        <Popup
          onClose={() => {
            setShowPopup(false);
            cancelEdit();
          }}
          title={isEditing ? "Edit Medium" : "Add New Medium"}
          maxWidth="500px"
        >
          {/* NAME */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Medium Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter medium name"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            />
          </div>

          {/* DESCRIPTION */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Description <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Enter description"
              rows="4"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
                resize: "vertical",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            />
          </div>

          {/* ACTIONS */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "16px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => {
                setShowPopup(false);
                cancelEdit();
              }}
              style={{
                padding: "10px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.target.style.backgroundColor = "#f9fafb")
              }
              onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "white",
                backgroundColor: isFormValid ? "#3b82f6" : "#9ca3af",
                cursor: isFormValid ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                if (isFormValid) e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (isFormValid) e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              {isEditing ? "Update Medium" : "Save Medium"}
            </button>
          </div>
        </Popup>
      )}

      {/* CLASS POPUP */}
      {showClassPopup && (
        <Popup
          onClose={() => {
            setShowClassPopup(false);
            setClassName("");
            setSelectedMediumId(null);
            setSelectedMediumName("");
          }}
          title={`Create Class for "${selectedMediumName}" Medium`}
          maxWidth="400px"
        >
          {/* CLASS NAME */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Class Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              placeholder="Enter class name"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            />
          </div>

          {/* ACTIONS */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "16px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => {
                setShowClassPopup(false);
                setClassName("");
                setSelectedMediumId(null);
                setSelectedMediumName("");
              }}
              style={{
                padding: "10px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.target.style.backgroundColor = "#f9fafb")
              }
              onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleAddClass}
              disabled={!isClassFormValid}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "white",
                backgroundColor: isClassFormValid ? "#3b82f6" : "#9ca3af",
                cursor: isClassFormValid ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                if (isClassFormValid) e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (isClassFormValid) e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              Save Class
            </button>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default Medium;
